import { createContext, useContext, useState, useEffect, useRef, ReactNode, useCallback } from 'react';
import { apiClient } from '../services/api';
import { Instance, InstanceListResponse, InstanceWithRole } from '../types/api';
import { useAuth } from './useAuth';

const SETUP_ERROR_MESSAGE = 'Could not set up your space. Please try again.';

interface InstanceContextType {
  instances: Instance[];
  instancesWithRoles: InstanceWithRole[]; // Includes user_role information
  selectedInstanceId: number | null;
  isLoading: boolean;
  setupError: string | null;
  selectInstance: (instanceId: number | null) => void;
  refreshInstances: () => Promise<Instance[]>;
  silentRefreshInstances: () => Promise<void>;
  clearSetupError: () => void;
}

const InstanceContext = createContext<InstanceContextType | undefined>(undefined);

export const useInstance = (): InstanceContextType => {
  const context = useContext(InstanceContext);
  if (!context) {
    throw new Error('useInstance must be used within an InstanceProvider');
  }
  return context;
};

interface InstanceProviderProps {
  children: ReactNode;
}

const STORAGE_KEY = 'vocatree_selected_instance_id';

export const InstanceProvider = ({ children }: InstanceProviderProps) => {
  const { user } = useAuth();
  const [instances, setInstances] = useState<Instance[]>([]);
  const [instancesWithRoles, setInstancesWithRoles] = useState<InstanceWithRole[]>([]);
  const [selectedInstanceId, setSelectedInstanceId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [setupError, setSetupError] = useState<string | null>(null);
  const inFlightLoadRef = useRef<Promise<void> | null>(null);
  const latestInstancesRef = useRef<Instance[]>([]);

  const getDefaultCollectionName = useCallback((): string => {
    return 'My Vocabulary';
  }, []);

  const loadInstances = useCallback(async () => {
    // Single-flight: if a load (with possible ensure) is already running, wait for it then do a lightweight refetch only.
    // Prevents duplicate workspace/instance creation when effect runs twice (e.g. React Strict Mode) or refresh is called during load.
    const existing = inFlightLoadRef.current;
    if (existing) {
      await existing;
      try {
        const response = await apiClient.getInstances();
        const instanceList = response.items.map(item => item.instance);
        latestInstancesRef.current = instanceList;
        setInstances(instanceList);
        setInstancesWithRoles(response.items);
        const savedId = localStorage.getItem(STORAGE_KEY) ? parseInt(localStorage.getItem(STORAGE_KEY)!, 10) : null;
        if (instanceList.length === 0) {
          setSelectedInstanceId(null);
        } else {
          const validSavedId = savedId != null && instanceList.some(inst => inst.id === savedId) ? savedId : null;
          setSelectedInstanceId(validSavedId !== null ? validSavedId : instanceList[0].id);
          if (instanceList.length === 1 || validSavedId === null) {
            localStorage.setItem(STORAGE_KEY, instanceList[0].id.toString());
          }
        }
      } catch (_) {
        // Ignore; first load already handled state
      }
      return;
    }

    const promise = (async () => {
      try {
        setIsLoading(true);
        setSetupError(null);

        // 1. Fetch workspaces and auto-create one if empty
        let workplacesResponse = await apiClient.getWorkplaces();
      if (workplacesResponse.items.length === 0) {
        const createWorkspaceWithRetry = async (attempt: number): Promise<number> => {
          try {
            const created = await apiClient.createWorkplace({ name: 'My Workspace' });
            return created.id;
          } catch (err) {
            if (attempt < 1) return createWorkspaceWithRetry(attempt + 1);
            throw err;
          }
        };
        try {
          await createWorkspaceWithRetry(0);
          workplacesResponse = await apiClient.getWorkplaces();
        } catch (err) {
          console.error('Failed to create workspace:', err);
          setSetupError(SETUP_ERROR_MESSAGE);
          latestInstancesRef.current = [];
          setInstances([]);
          setInstancesWithRoles([]);
          setSelectedInstanceId(null);
          return;
        }
      }

      const workplaceId = workplacesResponse.items[0].workplace.id;

      // 2. Fetch instances and auto-create one if empty
      let response: InstanceListResponse = await apiClient.getInstances();
      if (response.items.length === 0) {
        const createInstanceWithRetry = async (attempt: number): Promise<void> => {
          try {
            await apiClient.createCollection({
              name: getDefaultCollectionName(),
              workplace_id: workplaceId,
            });
          } catch (err) {
            if (attempt < 1) return createInstanceWithRetry(attempt + 1);
            throw err;
          }
        };
        try {
          await createInstanceWithRetry(0);
          response = await apiClient.getInstances();
        } catch (err) {
          console.error('Failed to create instance:', err);
          setSetupError(SETUP_ERROR_MESSAGE);
          latestInstancesRef.current = [];
          setInstances([]);
          setInstancesWithRoles([]);
          setSelectedInstanceId(null);
          return;
        }
      }

      const instanceList = response.items.map(item => item.instance);
      latestInstancesRef.current = instanceList;
      setInstances(instanceList);
      setInstancesWithRoles(response.items);

      const savedInstanceId = localStorage.getItem(STORAGE_KEY);
      const savedId = savedInstanceId ? parseInt(savedInstanceId, 10) : null;

      if (instanceList.length === 0) {
        setSelectedInstanceId(null);
        localStorage.removeItem(STORAGE_KEY);
      } else if (instanceList.length === 1) {
        setSelectedInstanceId(instanceList[0].id);
        localStorage.setItem(STORAGE_KEY, instanceList[0].id.toString());
      } else {
        const validSavedId = savedId && instanceList.some(inst => inst.id === savedId);
        if (validSavedId) {
          setSelectedInstanceId(savedId);
        } else {
          setSelectedInstanceId(instanceList[0].id);
          localStorage.setItem(STORAGE_KEY, instanceList[0].id.toString());
        }
      }
    } catch (error: any) {
      console.error('Error loading instances:', error);
      setSetupError(SETUP_ERROR_MESSAGE);
      latestInstancesRef.current = [];
      setInstances([]);
      setInstancesWithRoles([]);
      setSelectedInstanceId(null);
    } finally {
      setIsLoading(false);
      inFlightLoadRef.current = null;
    }
    })();
    inFlightLoadRef.current = promise;
    await promise;
  }, []);

  const selectInstance = useCallback((instanceId: number | null) => {
    setSelectedInstanceId(instanceId);
    if (instanceId !== null) {
      localStorage.setItem(STORAGE_KEY, instanceId.toString());
      import('../utils/analytics').then(({ trackCollectionSelected }) => trackCollectionSelected(instanceId));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, []);

  const refreshInstances = useCallback(async (): Promise<Instance[]> => {
    await loadInstances();
    return latestInstancesRef.current;
  }, [loadInstances]);

  const silentRefreshInstances = useCallback(async (): Promise<void> => {
    try {
      const response = await apiClient.getInstances();
      const instanceList = response.items.map(item => item.instance);
      latestInstancesRef.current = instanceList;
      setInstances(instanceList);
      setInstancesWithRoles(response.items);
    } catch {
      // silent — don't affect loading or error state
    }
  }, []);

  const clearSetupError = useCallback(() => {
    setSetupError(null);
  }, []);

  // Load instances on mount (and after every login, since InstanceProvider mounts when authenticated)
  useEffect(() => {
    loadInstances();
  }, [loadInstances]);

  const value: InstanceContextType = {
    instances,
    instancesWithRoles,
    selectedInstanceId,
    isLoading,
    setupError,
    selectInstance,
    refreshInstances,
    silentRefreshInstances,
    clearSetupError,
  };

  return (
    <InstanceContext.Provider value={value}>
      {children}
    </InstanceContext.Provider>
  );
};
